import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { adminApi } from '@/services/adminApi'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import type { Organization } from '@/types/admin'
import {
  TRANSCRIPTION_PROVIDER_OPTIONS,
  buildTranscriptionProviderUpdate,
  coerceTranscriptionProvider,
  defaultModelFor,
  providerSupportsModel,
  type TranscriptionProvider,
} from '@/lib/transcriptionProvider'

interface Props {
  organization: Organization
  onSaved: () => Promise<void> | void
}

// Per-org batch STT/diarization provider. Independent of the org edit form on
// purpose: the rollout flips one org at a time and rolls back the same way, so
// this is its own save, not a field inside "Edit organization".
export function TranscriptionProviderCard({ organization, onSaved }: Props) {
  const savedProvider = coerceTranscriptionProvider(organization.transcriptionProvider)
  const savedModel = organization.transcriptionModel ?? ''
  const [provider, setProvider] = useState<TranscriptionProvider>(savedProvider)
  const [model, setModel] = useState(savedModel)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setProvider(savedProvider)
    setModel(savedModel)
  }, [savedProvider, savedModel])

  const update = buildTranscriptionProviderUpdate(organization.id, provider, model)
  const dirty = update.provider !== savedProvider || update.model !== (savedModel.trim() || null)

  const handleSave = async () => {
    if (!dirty || saving) return
    setSaving(true)
    try {
      await adminApi.setTranscriptionProvider(update)
      toast.success(`Transcription provider set to ${provider}`)
      await onSaved()
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to set transcription provider')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="lg:col-span-2">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Meeting Transcription Provider</h3>
        <p className="text-sm text-gray-500 mb-6 text-left">
          Batch speech-to-text and speaker diarization for this organization's meeting
          recordings. Applies to new runs only; a deployment-wide{' '}
          <code className="text-xs">NATIVE_BATCH_STT_PROVIDER</code> override, if set, wins.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Provider"
            value={provider}
            disabled={saving}
            onChange={e => {
              const next = e.target.value as TranscriptionProvider
              setProvider(next)
              setModel(next === savedProvider ? savedModel : '')
            }}
            options={TRANSCRIPTION_PROVIDER_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
          {providerSupportsModel(provider) && (
            <Input
              label="Model"
              value={model}
              disabled={saving}
              onChange={e => setModel(e.target.value)}
              placeholder={`${defaultModelFor(provider)} (default)`}
            />
          )}
        </div>
        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-gray-500">
            Current: <span className="font-medium text-gray-900">{savedProvider}</span>
            {providerSupportsModel(savedProvider) && (
              <> · {savedModel || `${defaultModelFor(savedProvider)} (default)`}</>
            )}
          </span>
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save provider'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
